import React, { useState } from 'react';
import { CheckIcon, Loader2Icon } from 'lucide-react';
type Status = 'idle' | 'submitting' | 'success' | 'error';
type FormState = {
  name: string;
  email: string;
  organisation: string;
  project: string;
  budget: string;
  timing: string;
};
const budgets = ['Under $100k', '$100k – $300k', '$300k – $1m', '$1m+', 'Not sure yet'];
const initialState: FormState = {
  name: '',
  email: '',
  organisation: '',
  project: '',
  budget: '',
  timing: ''
};
export function Contact() {
  const [form, setForm] = useState<FormState>(initialState);
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  function update(field: keyof FormState, value: string) {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
  }
  function validate() {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) next.name = 'Please tell us your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Please enter a valid email address.';
    if (form.project.trim().length < 20) next.project = 'A couple of sentences helps us reply properly.';
    return next;
  }
  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setStatus('submitting');
    try {
      await new Promise(resolve => setTimeout(resolve, 1100));
      setStatus('success');
      setForm(initialState);
    } catch {
      setStatus('error');
    }
  }
  const inputClass = 'w-full border-b border-line bg-transparent py-3 outline-none transition-colors placeholder:text-muted/60 focus:border-sage';
  return <div className="mx-auto max-w-page px-6 py-12 md:px-10 md:py-16" data-id="element-129" data-mp="26yijlw0b5l0g">
      <header className="flex flex-wrap items-end justify-between gap-6" data-id="element-130" data-mp="2bu7lw73ppg0g">
        <h1 className="font-display text-4xl font-bold uppercase leading-[0.9] md:text-7xl" data-id="element-131" data-mp="1m6kep4lig4ay">
          Start a
          <br data-id="element-132" data-mp="1h78vloxo8mq7" />
          <span className="text-lilac" data-id="element-133" data-mp="1h7qy33axfx6k">project</span>
        </h1>
        <div className="text-sm text-muted" data-id="element-134" data-mp="1w62st4eqovv3">
          <a href="mailto:info@heybreezy.co" className="font-display text-xl uppercase text-cream underline underline-offset-4 transition-colors hover:text-sage md:text-2xl" data-id="element-135" data-mp="1w4rrdb8ro075">
            info@heybreezy.co
          </a>
          <p className="mt-2" data-id="element-136" data-mp="yikc97eu6pgj">We reply to every brief within two working days.</p>
        </div>
      </header>

      <div className="mt-12 border-t border-line pt-10" data-id="element-137" data-mp="1d7ql7n3h7u1m">
        {status === 'success' ? <div role="status" className="rounded-[1.5rem] bg-sage p-8 text-ink md:p-12" data-id="element-138" data-mp="18foyt8clgmxv">
            <CheckIcon size={26} data-id="element-139" data-mp="kpun4u40h4f7" />
            <h2 className="mt-4 font-display text-3xl font-bold uppercase leading-[0.95] md:text-4xl" data-id="element-140" data-mp="kotcurutklyj">
              Thanks — that&apos;s with us
            </h2>
            <p className="mt-3 max-w-md leading-relaxed text-ink/75" data-id="element-141" data-mp="2re4sadh6s86u">
              One of us will read it properly and come back within two working
              days, usually with questions rather than a deck.
            </p>
            <button type="button" onClick={() => setStatus('idle')} className="mt-7 text-[0.7rem] uppercase tracking-micro underline underline-offset-4 hover:text-ink/60" data-id="element-142" data-mp="2hhryzgpjzwcm">
              Send another brief
            </button>
          </div> : <form onSubmit={handleSubmit} noValidate className="grid gap-8 md:grid-cols-12" data-id="element-143" data-mp="3f3pfj8kdhrlx">
            <div className="md:col-span-7 space-y-8" data-id="element-144" data-mp="3561gw7jnbzyh">
              <div className="grid gap-8 sm:grid-cols-2" data-id="element-145" data-mp="354glygtf6f03">
                <div data-id="element-146" data-mp="1pormwbgjhgve">
                  <label htmlFor="name" className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-147" data-mp="1po80oijuw6bk">
                    Name
                  </label>
                  <input id="name" value={form.name} onChange={e => update('name', e.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'name-error' : undefined} className={inputClass} placeholder="Your name" data-id="element-148" data-mp="29zlqjw4sswnj" />
                  {errors.name && <p id="name-error" className="mt-2 text-sm text-red" data-id="element-149" data-mp="2nxsg8ozlfqc4">
                      {errors.name}
                    </p>}
                </div>
                <div data-id="element-150" data-mp="2iww4jp902zw5">
                  <label htmlFor="email" className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-151" data-mp="2ex42lve43rzr">
                    Email
                  </label>
                  <input id="email" type="email" value={form.email} onChange={e => update('email', e.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} className={inputClass} placeholder="you@company.com" data-id="element-152" data-mp="13i6xzd5g2u7a" />
                  {errors.email && <p id="email-error" className="mt-2 text-sm text-red" data-id="element-153" data-mp="1qe5ljl81pob">
                      {errors.email}
                    </p>}
                </div>
              </div>

              <div className="grid gap-8 sm:grid-cols-2" data-id="element-154" data-mp="ar2kshmcpw2s">
                <div data-id="element-155" data-mp="arlsz6m7963m">
                  <label htmlFor="organisation" className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-156" data-mp="aozj30bu6ei8">
                    Company
                  </label>
                  <input id="organisation" value={form.organisation} onChange={e => update('organisation', e.target.value)} className={inputClass} placeholder="Optional" data-id="element-157" data-mp="2hha5s0uwjef9" />
                </div>
                <div data-id="element-158" data-mp="3ttni3h5n462k">
                  <label htmlFor="timing" className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-159" data-mp="3tu72r5mod8ka">
                    Timing
                  </label>
                  <input id="timing" value={form.timing} onChange={e => update('timing', e.target.value)} className={inputClass} placeholder="e.g. Fall 2026" data-id="element-160" data-mp="7kctcdo2p4as" />
                </div>
              </div>

              <div data-id="element-161" data-mp="ceqpwjeh1hh1">
                <label htmlFor="project" className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-162" data-mp="qc5mq9720rj1">
                  What are you planning?
                </label>
                <textarea id="project" rows={5} value={form.project} onChange={e => update('project', e.target.value)} aria-invalid={Boolean(errors.project)} aria-describedby={errors.project ? 'project-error' : undefined} className={`${inputClass} resize-none`} placeholder="The audience, the occasion, and what you want them to walk away with." data-id="element-163" data-mp="14yulg8thf0od" />
                {errors.project && <p id="project-error" className="mt-2 text-sm text-red" data-id="element-164" data-mp="r1frqrw746ql">
                    {errors.project}
                  </p>}
              </div>
            </div>

            <div className="md:col-span-4 md:col-start-9" data-id="element-165" data-mp="h9t7k61vqxbp">
              <fieldset data-id="element-166" data-mp="39ifx0sqxeph">
                <legend className="text-[0.6rem] uppercase tracking-micro text-muted" data-id="element-167" data-mp="3aabibmcfcfr">
                  Budget range
                </legend>
                <div className="mt-4 flex flex-wrap gap-2" data-id="element-168" data-mp="nquwd8tq8vo6">
                  {budgets.map(budget => {
                const active = form.budget === budget;
                return <button key={budget} type="button" onClick={() => update('budget', active ? '' : budget)} aria-pressed={active} className={`rounded-full border px-4 py-2 text-[0.6rem] uppercase tracking-micro transition-colors ${active ? 'border-sage bg-sage text-ink' : 'border-line text-muted hover:border-cream hover:text-cream'}`} data-id="element-169" data-mp="dsoxz174q7o2">
                        {budget}
                      </button>;
              })}
                </div>
              </fieldset>

              {status === 'error' && <p role="alert" className="mt-6 text-sm text-red" data-id="element-170" data-mp="3d9wwpn3tc85e">
                  Something went wrong sending that. Please email us directly at
                  info@heybreezy.co.
                </p>}

              <button type="submit" disabled={status === 'submitting'} className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-lilac px-8 py-4 text-[0.65rem] uppercase tracking-micro text-ink transition-colors hover:bg-sage disabled:opacity-60" data-id="element-171" data-mp="21qpoucb8tt7n">
                {status === 'submitting' && <Loader2Icon size={14} className="animate-spin" data-id="element-172" data-mp="1dyj121jl2uju" />}
                {status === 'submitting' ? 'Sending' : 'Send brief'}
              </button>

              <p className="mt-6 text-sm leading-relaxed text-muted" data-id="element-173" data-mp="1nrzsiyw8zxkc">
                National tours and activations, coast to coast — routing,
                permitting, freight and on-site crew handled in-house.
              </p>
            </div>
          </form>}
      </div>
    </div>;
}